import { useState } from 'react'

import { useAtomValue } from 'jotai'
import {
  Text,
  GreyGrey80,
  Primary13Regular,
  useNotifications,
  SimpleModal,
  Flex
} from '@sequencehq/core-components'
import { useSaveAutomationVersion } from 'Automations/automationEditor/hooks'
import { useAutomationId } from 'Automations/automationEditor/hooks'
import { hasPresentationChangedAtom } from 'Automations/automationEditor/atoms'
import { useLogEvent } from 'lib/postHog/useLogEvent'
import { ChangelogField } from './ChangelogField'

export const ModalSaveVersion = ({ onClose }: { onClose: () => void }) => {
  const { displayNotification } = useNotifications()
  const { logEvent } = useLogEvent()
  const { isNewAutomation: _isNewAutomation } = useAutomationId()
  const [isNewAutomation] = useState(_isNewAutomation)
  const saveVersion = useSaveAutomationVersion()
  const [hasPresentationChanged] = useState(
    useAtomValue(hasPresentationChangedAtom)
  )

  const [changelog, setChangelog] = useState<string>('')

  const showChangelogField = !isNewAutomation && hasPresentationChanged

  return (
    <SimpleModal
      variant="dialog"
      isOpen
      onClose={onClose}
      width="436px"
      title="Save workflow"
      data-testid="automations.editor.saveModal"
      onSubmit={async () => {
        try {
          await saveVersion({ changelog })
          displayNotification('Workflow saved', { type: 'success' })
          return true
        } catch (error) {
          // Saving transforms the presentation (presentationToConductorWorkflow
          // et al.), which throws on invalid designs before we reach the server.
          // Surface the cause for debugging and emit a Posthog event so we can
          // track save failures in the wild.
          console.error(error)
          logEvent({
            eventName: 'automation_save_failed',
            metadata: {
              message: error instanceof Error ? error.message : String(error)
            }
          })
          displayNotification('Failed to save workflow', { type: 'error' })
          return false
        }
      }}
      submitButtonText="Save"
      submittingButtonText="Saving..."
      disableSubmitOnEnter
    >
      <Flex flexDir="column" gap="16px">
        <Text
          {...Primary13Regular}
          color={GreyGrey80}
          data-testid="automations.editor.saveModal.description"
        >
          {hasPresentationChanged
            ? 'An unpublished version of your workflow will be saved. It can be published later.'
            : 'Your workflow details will be updated.'}
        </Text>

        {showChangelogField && (
          <ChangelogField changelog={changelog} setChangelog={setChangelog} />
        )}
      </Flex>
    </SimpleModal>
  )
}
